import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

// These strings should match entity ID coming from backend services
export enum MigrationEntity {
	Contact = 'CONTACT',
	Office = 'OFFICE',
	Client = 'CLIENT'
}

export enum ConflictResolution {
	Resolved = 'RESOLVED',
	Unresolved = 'UNRESOLVED'
}

export interface MigrationEntityConfig {
	conflictServiceUrl: string; // Relative to primary domain (e.g. demo.onvio.us)
	translationKey: string;
	// Property information. The order of the array represents the order columns are displayed in the grid
	properties: { name: string; translationKey: string }[];
}

const migrationEntityConfigurations: {[key in MigrationEntity]?: MigrationEntityConfig} = {
	[MigrationEntity.Contact]: {
		conflictServiceUrl: '/path/to/contact-conflict/service',
		translationKey: 'Core.Captions.Contact.ClassOne',
		properties: [
			{
				name: 'name',
				translationKey: 'Core.Captions.Contact.FullName'
			}, {
				name: 'email',
				translationKey: 'Core.Captions.ContactEmail.EmailAddress'
			}, {
				name: 'nid',
				translationKey: 'Core.Captions.NationalIdentity.ClassOne'
			}
		]
	},
	[MigrationEntity.Client]: {
		conflictServiceUrl: '/path/to/contact-conflict/service',
		translationKey: 'Core.Captions.Client.ClassOne',
		properties: [
			{
				name: 'id',
				// TODO: should be changed to 'code' because:
				// in backend logic ContactConflictServices (ClientMatchService.cs line 120) id is assigned as code
				translationKey: 'Core.Captions.Client.Code'
			}, {
				name: 'name',
				translationKey: 'Core.Captions.Contact.FullName'
			}, {
				name: 'email',
				translationKey: 'Core.Captions.ContactEmail.EmailAddress'
			}, {
				name: 'nid',
				translationKey: 'Core.Captions.NationalIdentity.ClassOne'
			}
		]
	},
	[MigrationEntity.Office]: {
		conflictServiceUrl: '/path/to/contact-conflict/service',
		translationKey: 'Core.Captions.Office.ClassOne',
		properties: [
			{
				name: 'code',
				translationKey: 'Core.Captions.Office.Code'
			}, {
				name: 'name',
				translationKey: 'Core.Captions.Contact.FullName'
			},
		]
	}
};

export interface ConflictChoice {
	selected: boolean;
	data: {[key: string]: string};
}

export interface IConflict {
	// From MigrationResultsService API
	conflictId: string;
	migrationId: string;
	conflictEntityType: MigrationEntity;
	conflictStatus: string;
	conflictResolution: string;
	sourceEntityId: string;
	sourceEntityName: string;
	conflictCreatedDate: string;
	conflictChangeDate: string;
	sourceEntityData: {[key: string]: string};
	matches: ConflictChoice[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Conflict extends IConflict {}
export class Conflict {

	private _choice$ = new BehaviorSubject<number>(undefined);
	private _conflictStatus$ = new BehaviorSubject<string>(undefined);

	conflictEntityConfig?: MigrationEntityConfig;

	choice$: Observable<number>;
	conflictResolution$: Observable<string>;
	isResolved$: Observable<boolean>;
	resolutionTextKey$: Observable<string>;
	resolutionInfoTextKey$: Observable<string>;
	statusTextKey$: Observable<string>;

	resolvedOnUpdate = false;

	public refreshing = false;

	constructor(dto: IConflict) {

		Object.defineProperty(this, 'choice', {
			set: (value: number): void => {
				if (value === this._choice$.value) {
					return;
				}
				this._choice$.next(value);
				this._onChoiceChange();
			},
			get: (): number => this._choice$.value
		});

		Object.defineProperty(this, 'conflictStatus', {
			set: (value: string): void => {
				if (value === this._conflictStatus$.value) {
					return;
				}
				this._conflictStatus$.next(value);
			},
			get: (): string => this._conflictStatus$.value
		});

		Object.assign(this, dto);

		this.conflictEntityConfig = migrationEntityConfigurations[this.conflictEntityType];

		this.choice$ = this._choice$.asObservable().pipe(
		);

		this.isResolved$ = this._conflictStatus$.pipe(
			map(status =>
				status === ConflictResolution.Resolved ? true : false
			)
		);

		this.statusTextKey$ = this.choice$.pipe(map(() =>
			this.conflictStatus === ConflictResolution.Resolved ? 'onvio.setup.migrations.isResolved' : 'onvio.setup.migrations.unresolved'
		));

		this.resolutionTextKey$ = this.choice$.pipe(map(choice => {
			switch (choice) {
			case undefined:
				return 'onvio.setup.migrations.noneSelected';
			case -1:
				return 'onvio.setup.migrations.createNew';
			default:
				return 'onvio.setup.migrations.matchRecordNumber';
			}
		}));

		this.resolutionInfoTextKey$ = this.choice$.pipe(map(choice => {
			switch (choice) {
			case undefined:
				return 'onvio.setup.migrations.unresolved';
			case -1:
				return 'onvio.setup.migrations.createNewDescription';
			default:
				return 'onvio.setup.migrations.matchDescription';
			}
		}));

		this.choice = this.conflictResolution?.toUpperCase() === 'CREATE' ? -1 :
			this.conflictResolution?.toUpperCase() === 'MATCH' ? this.matches.findIndex(match => match.selected) :
				undefined;

		this.resolvedOnUpdate = this.choice !== undefined;
	}

	choice: number;

	conflictStatus: string;

	conflictResolution: string;

	private _onChoiceChange() {
		this.matches?.forEach(match => match.selected = false);

		if (this.choice === undefined) {
			this.conflictStatus = ConflictResolution.Unresolved;
			this.conflictResolution = undefined;
		} else {
			this.conflictStatus = ConflictResolution.Resolved;
			if (this.choice === -1) {
				this.conflictResolution = 'create';
			} else {
				this.matches[this.choice].selected = true;
				this.conflictResolution = 'match';
			}
		}
	}

	isResolved() {
		return this.conflictStatus === ConflictResolution.Resolved;
	}

	serialize(): IConflict {
		return {
			conflictId: this.conflictId,
			migrationId: this.migrationId,
			conflictEntityType: this.conflictEntityType,
			conflictStatus: this.conflictStatus,
			conflictResolution: this.conflictResolution,
			sourceEntityId: this.sourceEntityId,
			sourceEntityName: this.sourceEntityName,
			conflictCreatedDate: this.conflictCreatedDate,
			conflictChangeDate: this.conflictChangeDate,
			sourceEntityData: this.sourceEntityData,
			matches: this.matches,
		};
	}
}
